import { Injectable } from '@angular/core';
import {
  DocumentManagementApiService,
  DocumentSearch,
  GetShipmentImagedDocumentsQuery,
  GetShipmentImagedDocumentsResp,
  GetShipmentImagedDocumentsRqst,
  RetrieveDmsAuthTokenResp,
  SearchDmsDocumentResp,
  SearchDmsDocumentRqst
} from '@xpo-ltl/sdk-documentmanagement';
import {
  CreateArchiveDmsDocumentResp,
  DocumentInfo,
  InspectionContext,
  InspectionHandlingUnit,
  InspectionShipment
} from '@xpo-ltl/sdk-inspections';
import * as moment from 'moment';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/internal/operators';
import { Photo } from '../classes/photo';
import { PhotoId } from '../classes/photo-id';
import { ProNumber } from '../classes/pronumber';
import { DocTypes } from '../enums/doc-type.enum';
import { ErrorMessageActions } from '../enums/error-message-actions.enum';
import { RetryStrategy } from '../operators/retry-strategy';
import { RequestValidator } from '../validators/request.validator';
import { AppConstantsService } from './app-constants.service';
import { ErrorHandlingService } from './error-handling.service';
import { InspectionsApiWrapperService } from './inspections/inspections.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentManagementService {
  private static DEFAULT_GET_RETRY_COUNT = 4;
  private static DEFAULT_GET_TIMEOUT = 15000;
  private static DEFAULT_UPDATE_TIMEOUT = 120000; // 2 min max transfer per image
  private static DEFAULT_UPDATE_RETRY_COUNT = 0; // don't retry on update (System will automatically)
  private static DEFAULT_ERROR_RETRY_DELAY = 1000; // retry every second on (non-timeout) errors
  private readonly ERROR_GETTING_PHOTO: string = 'Error Getting Photo!';
  private readonly IMAGE_FORMAT: string = 'JPEG';
  private readonly MULTI_PART_RESP: string = 'false';
  private dmsToken: string;
  private dmsTokenExpiration: number;

  constructor(
    private documentManagement: DocumentManagementApiService,
    private inspectionsService: InspectionsApiWrapperService,
    private constants: AppConstantsService,
    private retryStrategy: RetryStrategy,
    private errorHandling: ErrorHandlingService
  ) {}

  /**
   * save Inspections Photos
   * we're calling Inspections API to post photos since calling DMS directly from UI fails.
   * failing response - 415 Unsupported Media Type.
   * asked DMS team, but no response so we'll continue calling DMS from our backend side.
   *
   * @param photo
   */
  createArchiveDmsDocument(photo: Photo): Observable<DocumentInfo> {
    RequestValidator.validateObjectNotUndefinedOrEmpty(photo, 'Photo');

    return this.constants.inspectionContext$.pipe(
      take(1),
      switchMap((context: InspectionContext) => {
        RequestValidator.validateStringNotNullOrEmpty(context.inspectionSic, 'Inspection SIC');

        return this.inspectionsService.createArchiveDmsDocument(photo, this.constants.sicCode).pipe(
          this.retryStrategy.retryStrategy(
            DocumentManagementService.DEFAULT_UPDATE_TIMEOUT,
            DocumentManagementService.DEFAULT_ERROR_RETRY_DELAY,
            DocumentManagementService.DEFAULT_UPDATE_RETRY_COUNT,
            null,
            'CREATE-ARCHIVE-DMS-DOCUMENT'
          ),
          take(1),
          map((response: CreateArchiveDmsDocumentResp) => {
            if (response?.documentInfo?.documentUrl?.trim().length > 0) {
              return response.documentInfo;
            } else {
              throw new Error(`Create Archive DMS Document API returned: ${response}`);
            }
          }),
          catchError((error) => {
            this.errorHandling.handleResponseError(error, ErrorMessageActions.CREATING, `photo`);

            return EMPTY;
          })
        );
      })
    );
  }

  listDocumentIds(proNumber: ProNumber): Observable<PhotoId[]> {
    RequestValidator.validateProNumber(proNumber);

    return this.getDmsAuth().pipe(
      take(1),
      switchMap((token: string) => {
        return this.searchDmsDocument(token, proNumber);
      })
    );
  }

  /**
   * this will be called by parent PRO first, and then if there are child PROs that have dimensioner photos,
   * we'll call this recursively with the child PROs and save the parent PRO
   * @param dmsToken
   * @param proNumber
   * @param parentProNbr
   */
  searchDmsDocument(dmsToken: string, proNumber: ProNumber, parentProNbr?: ProNumber): Observable<PhotoId[]> {
    const currentShipmentDetails: InspectionShipment = this.constants.inspectionShipmentDetails.shipmentDetails;
    const searchRequest: SearchDmsDocumentRqst = new SearchDmsDocumentRqst();
    searchRequest.searchString = proNumber.getRawProNumber();
    searchRequest.corpCode = this.constants.imageCorpCode;
    searchRequest.dmsAuth = dmsToken;
    // dmsAuth must be manually added to the header
    // Client SDK doesn't quite handle it properly at this time
    return this.documentManagement
      .searchDmsDocument(searchRequest, { loadingOverlayEnabled: false }, { headers: { dmsAuth: dmsToken } })
      .pipe(
        this.retryStrategy.retryStrategy(
          DocumentManagementService.DEFAULT_GET_TIMEOUT,
          DocumentManagementService.DEFAULT_ERROR_RETRY_DELAY,
          DocumentManagementService.DEFAULT_GET_RETRY_COUNT,
          null,
          'SEARCH-DMS-DOCUMENT'
        ),
        take(1),
        switchMap((searchResponse: SearchDmsDocumentResp) => {
          const photoIds: PhotoId[] = new Array<PhotoId>();
          const documentInfoArray: DocumentSearch[] = searchResponse?.documentInfo;
          if (documentInfoArray?.length > 0) {
            // create PhotoIds based on response
            documentInfoArray.forEach((documentInfo) => {
              photoIds.push(new PhotoId(proNumber, documentInfo.cdt.docClass, documentInfo.cdt.timestamp));
            });

            if (proNumber.isChildPro(proNumber.getRawProNumber())) {
              // if proNumber is a child PRO, save parent PRO and return
              RequestValidator.validateProNumber(parentProNbr);

              photoIds.forEach((photoId: PhotoId) => {
                photoId.parentProNumber = parentProNbr;
              });
              return of(photoIds);
            } else if (proNumber.isParentPro(proNumber.getRawProNumber())) {
              // if proNumber is a parent PRO and any child PRO has dimensioner photos,
              // call searchDmsDocument recursively with child PROs
              if (
                photoIds.some((photoId: PhotoId) => photoId.imageType === DocTypes.PICKUP_DIMENSIONER_PHOTO) &&
                currentShipmentDetails.inspectionHandlingUnits?.length > 0
              ) {
                return forkJoin(
                  currentShipmentDetails.inspectionHandlingUnits.map(
                    (inspectionHandlingUnit: InspectionHandlingUnit) => {
                      const childPro: ProNumber = new ProNumber(inspectionHandlingUnit.handlingUnitProNbr);
                      return this.searchDmsDocument(dmsToken, childPro, proNumber);
                    }
                  )
                ).pipe(
                  map((resultIds: PhotoId[][]) => {
                    // flattening the response from PhotoId[][] to PhotoId[]
                    return ([] as PhotoId[]).concat(...resultIds);
                  })
                );
              } else {
                return of(photoIds);
              }
            } else {
              return of(photoIds);
            }
          } else {
            return of(photoIds);
          }
        }),
        catchError((error) => {
          this.errorHandling.handleResponseError(error, ErrorMessageActions.GETTING, `photo ID`);

          return EMPTY;
        })
      );
  }

  /**
   * get Documents/Photo
   * getShipmentImagedDocuments is the only endpoint that can handle all the file types(JPEG, PDF, TIFF, PNG)
   * getArchiveDocument and getDocument have limitation see more info in LEI-1407
   *
   * @param photoId
   */
  getShipmentImagedDocuments(photoId: PhotoId): Observable<Photo> {
    RequestValidator.validateObjectNotUndefinedOrEmpty(photoId, 'Photo ID');

    return this.getDmsAuth().pipe(
      take(1),
      switchMap((token: string) => {
        const request = new GetShipmentImagedDocumentsRqst();
        // if parent PRO exists, that means its a Child PRO. we need to request with parent PRO.
        if (photoId.parentProNumber) {
          request.proNbr = photoId.parentProNumber.formatProNumber();
        } else {
          request.proNbr = photoId.proNumber.formatProNumber();
        }
        request.dmsAuth = token;
        const query = new GetShipmentImagedDocumentsQuery();
        query.imageFormat = this.IMAGE_FORMAT;
        query.multiPartResp = this.MULTI_PART_RESP;
        query.imageType = [photoId.imageType];
        query.docArchiveTimestamp = photoId.id;

        return this.handleGetShipmentImagedDocuments(
          this.documentManagement.getShipmentImagedDocuments(request, query, { loadingOverlayEnabled: false }),
          photoId
        );
      })
    );
  }

  /**
   * get DMS token when its first time calling or expired.
   */
  private getDmsAuth(): Observable<string> {
    if (this.dmsTokenExpiration > Math.ceil(moment().valueOf() / 100)) {
      return of(this.dmsToken);
    } else {
      return this.documentManagement.retrieveDmsAuthToken({ loadingOverlayEnabled: false }).pipe(
        this.retryStrategy.retryStrategy(
          DocumentManagementService.DEFAULT_GET_TIMEOUT,
          DocumentManagementService.DEFAULT_ERROR_RETRY_DELAY,
          DocumentManagementService.DEFAULT_GET_RETRY_COUNT,
          null,
          'GET_DMS_AUTH_TOKEN'
        ),
        take(1),
        map((token: RetrieveDmsAuthTokenResp) => {
          if (token?.access_token) {
            this.dmsToken = token.access_token;
            this.dmsTokenExpiration = Math.ceil(moment().valueOf() / 100) + token.expires_in;
            return token.access_token;
          } else {
            throw new Error(`Retrieve DMS auth token returned: ${token}`);
          }
        }),
        catchError((error) => {
          this.errorHandling.handleResponseError(error, ErrorMessageActions.GETTING, `DMS Auth Token`);

          return EMPTY;
        })
      );
    }
  }

  private getParsedImageDocumentsResponse(images: any, aPhotoId: PhotoId): Photo[] {
    const photos = new Array<Photo>();

    if (!images) {
      return photos;
    }

    const data = atob(images['binary']);
    if (!data) {
      return photos;
    }

    // parse the first line for the boundary
    // we just want the JSON from the multi part. Based on the query.multiPartResp = 'false', there should only be one JSON object
    const leftBracketIndex = data.indexOf('{');
    const rightBracketIndex = data.lastIndexOf('}') + 1;
    if (leftBracketIndex >= 0 && rightBracketIndex > leftBracketIndex) {
      const json = data.substring(leftBracketIndex, rightBracketIndex + 1);
      const photoObjects = JSON.parse(json);

      const photoData = photoObjects['data'];
      const imagedDocuments = <Array<any>>photoData['imagedDocument'];
      imagedDocuments.forEach((imagedDocument) => {
        const imageFiles = <Array<any>>imagedDocument['imageFiles'];
        imageFiles.forEach((imageFile) => {
          const photo = new Photo(aPhotoId);
          photo.fileName = imageFile['fileName'];
          photo.setBase64dataAndContentType(imageFile['base64Data'], imageFile['contentType']);
          photos.push(photo);
        });
      });
      return photos;
    } else {
      return photos;
    }
  }

  private handleGetShipmentImagedDocuments(observable: Observable<any>, photoId: PhotoId): Observable<Photo> {
    return observable.pipe(
      take(1),
      map((resp: GetShipmentImagedDocumentsResp) => {
        const photos: Photo[] = this.getParsedImageDocumentsResponse(resp, photoId); // unfortunately, dmsDocTimestamp is not part of the response
        if (photos?.length > 0) {
          const photo = photos[0];
          if (photo?.isLoaded && photo.getBase64data(false)?.trim()?.length > 0) {
            return photo;
          } else {
            throw new Error(this.ERROR_GETTING_PHOTO);
          }
        } else {
          throw new Error(this.ERROR_GETTING_PHOTO);
        }
      }),
      catchError((error) => {
        // Return Photo with no bytes so page shows "Error" and not trying to still load
        const emptyPhoto = new Photo(photoId);
        emptyPhoto.setBase64dataAndContentType('');

        this.errorHandling.handleResponseError(error, ErrorMessageActions.GETTING, `Imaged Document`);

        return EMPTY;
      })
    );
  }
}
